<template>
  <v-layout
    row
    justify-center
  >
    <v-dialog
      v-model="isOpen"
      persistent
      max-width="1000px"
    >
      <v-card class="create-bridge-signing">
        <v-form>
          <v-card-title class="toolbar primary white--text">
            <v-icon dark>
              note_add
            </v-icon>
            <h3
              v-if="!taxTrait.user_id"
              class="title ml-2"
            >
              Create Tax Trait
            </h3>
            <h3
              v-if="taxTrait.user_id"
              class="title ml-2"
            >
              Update Tax Trait
            </h3>
            <v-spacer />
            <v-btn
              color="secondary"
              flat
              @click="onToggleOpen"
            >
              X
            </v-btn>
          </v-card-title>
          <v-progress-linear
            v-if="isLoading"
            :indeterminate="true"
            class="ma-0"
          />

          <v-card-text>
            <v-container
              grid-list-md
              class="pa-0"
            >
              <v-layout
                wrap
                class="form-group"
              >
                <v-flex xs12>
                  <h6 class="subheading">
                    Tax Trait Details
                  </h6>
                </v-flex>

                <v-flex
                  xs12
                  md12
                >
                  <v-text-field
                    v-model="formData.user_id"
                    label="User ID"
                    :error-messages="getFieldErrors('user_id')"
                    :disabled="!!taxTrait.user_id"
                    @input="$v.formData.user_id.$touch()"
                    @blur="$v.formData.user_id.$touch()"
                  />
                </v-flex>

                <v-flex
                  xs12
                  md12
                >
                  <h3 class="text-xs-left">
                    Identity
                  </h3>
                </v-flex>

                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.taxable_in"
                    label="Taxable In"
                    :error-messages="getFieldErrors('taxable_in')"
                    @input="$v.formData.taxable_in.$touch()"
                    @blur="$v.formData.taxable_in.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.tin"
                    label="TIN"
                    :error-messages="getFieldErrors('tin')"
                    @input="$v.formData.tin.$touch()"
                    @blur="$v.formData.tin.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.church_tax_rate"
                    label="Church Tax Rate"
                    :error-messages="getFieldErrors('church_tax_rate')"
                    @input="$v.formData.church_tax_rate.$touch()"
                    @blur="$v.formData.church_tax_rate.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.church_denomination"
                    label="Church Denomination"
                    :error-messages="getFieldErrors('church_denomination')"
                    @input="$v.formData.church_denomination.$touch()"
                    @blur="$v.formData.church_denomination.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.church_religion_key"
                    label="Church Religion Key"
                    :error-messages="getFieldErrors('church_religion_key')"
                    @input="$v.formData.church_religion_key.$touch()"
                    @blur="$v.formData.church_religion_key.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-select
                    v-model="formData.is_legal_entity"
                    :items="booleanYesNo()"
                    label="Legal Entity?"
                    item-key="id"
                    item-text="name"
                    item-value="id"
                    :error-messages="getFieldErrors('is_legal_entity')"
                    @input="$v.formData.is_legal_entity.$touch()"
                    @blur="$v.formData.is_legal_entity.$touch()"
                  />
                </v-flex>
              </v-layout>
            </v-container>
            <v-alert
              v-for="e in error"
              :key="e.path"
              :value="error"
              type="error"
              color="error"
            >
              {{ e.message }}
            </v-alert>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              color="secondary"
              flat
              @click="onToggleOpen"
            >
              Cancel
            </v-btn>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  :disabled="isLoading || !$v.formData.$anyDirty"
                  :loading="isLoading"
                  color="primary"
                  @click="submit"
                  v-on="on"
                >
                  <span v-if="!taxTrait.user_id">Create Tax Trait</span>
                  <span v-if="taxTrait.user_id">Update Tax Trait</span>
                </v-btn>
              </template>
              <span>Validate and submit</span>
            </v-tooltip>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { minLength, maxLength, required } from 'vuelidate/lib/validators';

export default {
  name: 'CreateDialog',
  props: {
    taxTrait: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    toggleOpen: {
      type: Function,
      required: true,
    },
  },
  data: () => ({
    isOpenProp: this.isOpen,
    isLoading: false,
    taxableInSelections: ['DEU'],
    error: null,
    formData: {
      user_id: null,
      taxable_in: '',
      church_religion_key: '',
      church_tax_rate: '',
      church_denomination: '',
      tin: '',
      is_legal_entity: false,
    },
  }),
  validations() {
    const allowedTinLength = this.formData.is_legal_entity ? 10 : 11;

    return {
      formData: {
        user_id: { required },
        taxable_in: { minLength: minLength(3), maxLength: maxLength(3) },
        tin: { minLength: minLength(allowedTinLength), maxLength: maxLength(allowedTinLength) },
        church_religion_key: { minLength: minLength(5), maxLength: maxLength(5) },
      },
    };
  },
  computed: {
    validFields() {
      return Object.keys(this.$v.formData.$params).filter(
        item => !this.$v.formData[item].$invalid,
      );
    },
    errors() {
      return Object.keys(this.$v.formData.$params).filter(
        item => this.$v.formData[item].$invalid,
      );
    },
  },
  mounted() {
    this.formData = {
      ...this.formData,
      ...(this.serializeTaxTrait(this.taxTrait)),
    };

    this.$v.$touch();
  },
  methods: {
    booleanYesNo() {
      return [
        { id: true, name: 'Yes' },
        { id: false, name: 'No' },
      ];
    },
    getFieldErrors(field) {
      const errors = [];
      const formField = this.$v.formData[field];

      if (formField) {
        if (!formField.$dirty || !formField.$invalid) {
          return errors;
        }

        if (!formField.required) {
          errors.push('Required field');
        }

        if (!formField.minLength) {
          errors.push('Field has a minimum length');
        }

        if (!formField.maxLength) {
          errors.push('Field has a maximum length');
        }
      }

      return errors;
    },
    onToggleOpen() {
      this.toggleOpen();
      this.error = null;
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      this.isLoading = true;
      if (this.taxTrait.user_id) {
        this.$store
          .dispatch('updateTaxTrait', this.serializeFormData(this.formData))
          .then(() => {
            this.onToggleOpen();
          })
          .catch((err) => {
            this.error = err;
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        this.$store
          .dispatch('createTaxTrait', this.serializeFormData(this.formData))
          .then(() => {
            this.onToggleOpen();
          })
          .catch((err) => {
            this.error = err;
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    serializeTaxTrait(taxTrait) {
      const { ...serializeTaxTrait } = taxTrait;

      serializeTaxTrait.taxable_in = serializeTaxTrait.taxable_in || '';
      serializeTaxTrait.church_religion_key = serializeTaxTrait.church_religion_key || '';
      serializeTaxTrait.church_tax_rate = serializeTaxTrait.church_tax_rate || '';
      serializeTaxTrait.church_denomination = serializeTaxTrait.church_denomination || '';
      serializeTaxTrait.tin = serializeTaxTrait.tin || '';

      return {
        ...serializeTaxTrait,
      };
    },
    serializeFormData(formData) {
      const { ...serializeFormData } = formData;

      return {
        ...serializeFormData,
      };
    },
  },
};
</script>

<style lang="scss">
    .create-bridge-signing {
        .v-card__title {
            padding: 24px;
        }
        .v-card__text {
            max-height: 65vh;
            overflow: auto;
            padding-top: 32px;
        }
        .v-card__actions {
            border-top: 1px solid rgba(0, 0, 0, 0.12);
        }
        .radio-label {
            position: absolute;
            font-size: 16px;
            text-align: left;
            max-width: 133%;
            height: 20px;
            left: 12px;
            line-height: 20px;
            color: rgba(0, 0, 0, 0.54);
            -webkit-transform: translateY(-18px) scale(0.75);
            transform: translateY(0px) translateX(-6px) scale(0.75);
        }
        .subheading {
            padding: 8px;
            background: white;
            color: rgba(0, 0, 0, 0.54);
            top: -20px;
            left: 8px;
            position: absolute;
            display: flex;
            align-items: center;
        }
        .form-group {
            padding: 16px;
            margin: 0 0 24px 0;
            border: 1px solid rgba(0, 0, 0, 0.12);
            border-radius: 5px;
            position: relative;
        }
        .container.grid-list-md .layout:not(:only-child) {
            margin: 0 0 24px 0;
        }
        .v-input--selection-controls.v-input .v-label {
            text-transform: capitalize;
        }
    }
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/dialogs/taxTrait/CreateDialog.vue